<template>
  <v-container fluid class="job-page">
    <AError :api="apiGetXeroToken" />

    <!-- Alert Banner -->
    <AAlertBanner variant="danger" :messages="alertError" title="Warning" :icon-size="22" />

    <!-- Header -->
    <div class="job-header">
      <div class="job-header__top">
        <div class="job-header__info" v-if="!loadingDialog">
          <h1 class="job-header__title">{{ model.name.xero_token }}</h1>
        </div>
        <div class="job-header__actions">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <button
                class="hero-action-btn hero-action-btn--blue"
                v-bind="attrs"
                v-on="on"
                @click="generateXeroToken()"
              >
                <v-icon size="18">mdi-shield-plus</v-icon>
              </button>
            </template>
            <span>Generate XERO Token</span>
          </v-tooltip>
        </div>
      </div>
    </div>

    <!-- Tab toggle -->
    <div class="tab-bar">
      <button
        class="tab-btn"
        :class="{ 'tab-btn--active': activeTab === 'history' }"
        @click="activeTab = 'history'"
      >
        <v-icon size="16" class="mr-1">mdi-history</v-icon>
        XERO Token History
      </button>
      <button
        class="tab-btn"
        :class="{ 'tab-btn--active': activeTab === 'reminder' }"
        @click="activeTab = 'reminder'"
      >
        <v-icon size="16" class="mr-1">mdi-email-clock-outline</v-icon>
        Email Reminder
      </button>
    </div>

    <!-- Data Table Section -->
    <div class="section-card" v-show="activeTab === 'history'">
      <div class="section-header">
        <v-icon color="white" small>mdi-key-outline</v-icon>
        <span class="section-header__title">XERO Token History</span>
        <v-spacer></v-spacer>
        <span class="section-header__count">{{ xeroDatas.length }} records</span>
      </div>
      <div class="section-body section-body--table">
        <v-data-table
          :headers="headers"
          :items="xeroDatas"
          :items-per-page="10"
          class="job-table"
          :footer-props="{
            'items-per-page-options': [10, 25, 50],
          }"
        >
          <!-- Index column -->
          <template v-slot:item.index="{ item }">
            <span class="table-index">{{ item.index }}</span>
          </template>

          <!-- Valid From column -->
          <template v-slot:item.validFrom="{ item }">
            <div class="table-date">
              <v-icon size="14" color="#22c55e" class="mr-1">mdi-calendar-check-outline</v-icon>
              <span>{{ item.validFrom }}</span>
            </div>
          </template>

          <!-- Valid To column -->
          <template v-slot:item.validTo="{ item }">
            <div class="table-date">
              <v-icon size="14" color="#f59e0b" class="mr-1">mdi-calendar-clock-outline</v-icon>
              <span>{{ item.validTo }}</span>
            </div>
          </template>

          <!-- Generate Date column -->
          <template v-slot:item.timestampCreated="{ item }">
            <div class="table-date">
              <v-icon size="14" color="var(--app-text-faint)" class="mr-1">mdi-clock-outline</v-icon>
              <span>{{ item.timestampCreated }}</span>
            </div>
          </template>

          <!-- Error column -->
          <template v-slot:item.err="{ item }">
            <div class="table-error" v-html="item.err"></div>
          </template>

          <!-- Empty state -->
          <template v-slot:no-data>
            <div class="table-empty">
              <v-icon size="48" color="#cbd5e1">mdi-key-remove</v-icon>
              <span class="table-empty__text">No XERO token records found</span>
            </div>
          </template>
        </v-data-table>
      </div>
    </div>

    <!-- Email Reminder Section -->
    <div class="section-card" v-show="activeTab === 'reminder'">
      <div class="section-header">
        <v-icon color="white" small>mdi-email-clock-outline</v-icon>
        <span class="section-header__title">Email Reminder — XERO Token</span>
        <v-spacer></v-spacer>
        <span class="section-header__count section-header__status">
          <v-icon
            size="16"
            :color="reminder.enabled ? '#4ade80' : '#f87171'"
            class="mr-1"
          >{{ reminder.enabled ? "mdi-check-circle" : "mdi-close-circle" }}</v-icon>
          {{ reminder.enabled ? "Enabled" : "Disabled" }}
        </span>
      </div>
      <div class="section-body section-body--form">
        <AError :api="apiGetReminder" />

        <!-- Preview banner -->
        <div
          class="reminder-preview"
          :class="{ 'reminder-preview--off': !latestToken }"
        >
          <v-icon
            size="18"
            :color="latestToken ? '#0369a1' : '#94a3b8'"
            class="mr-2"
          >mdi-information-outline</v-icon>
          <span v-if="latestToken">
            Latest token <strong>Valid To {{ latestToken.validTo }}</strong> —
            reminder will be sent on
            <strong>{{ previewSendDate || "—" }}</strong>
            at <strong>{{ reminder.sendTime || "08:00" }}</strong>.
          </span>
          <span v-else>
            No XERO token found yet — generate a token before enabling reminders.
          </span>
        </div>

        <!-- Enable switch (only the switch toggles; the text is not clickable) -->
        <div class="form-row reminder-toggle">
          <v-switch
            v-model="reminder.enabled"
            inset
            hide-details
            color="var(--app-accent)"
            class="reminder-switch"
          ></v-switch>
          <span class="reminder-switch__text">
            {{ reminder.enabled ? "Reminder enabled" : "Reminder disabled" }}
          </span>
        </div>

        <div class="form-grid">
          <div class="form-field">
            <label class="form-label">Send before expiry</label>
            <v-text-field
              v-model.number="reminder.leadDays"
              type="number"
              min="0"
              max="60"
              dense
              outlined
              hide-details
              suffix="day(s) before"
            ></v-text-field>
          </div>
          <div class="form-field">
            <label class="form-label">Send out time</label>
            <div class="time-dropdowns">
              <v-select
                v-model="sendHour"
                :items="hourOptions"
                dense
                outlined
                hide-details
                append-icon="mdi-menu-down"
                class="time-select"
                :menu-props="{ offsetY: true }"
              ></v-select>
              <span class="time-colon">:</span>
              <v-select
                v-model="sendMinute"
                :items="minuteOptions"
                dense
                outlined
                hide-details
                append-icon="mdi-menu-down"
                class="time-select"
                :menu-props="{ offsetY: true }"
              ></v-select>
            </div>
          </div>
        </div>

        <div class="form-field">
          <label class="form-label">Email recipients</label>
          <v-combobox
            v-model="reminder.recipients"
            multiple
            chips
            small-chips
            deletable-chips
            dense
            outlined
            hide-details
            append-icon=""
            placeholder="Type an email and press Enter"
          ></v-combobox>
        </div>

        <div class="form-field">
          <label class="form-label">Email subject</label>
          <v-text-field
            v-model="reminder.subject"
            dense
            outlined
            hide-details
          ></v-text-field>
        </div>

        <div class="form-field">
          <label class="form-label">Email content</label>
          <v-textarea
            v-model="reminder.body"
            rows="7"
            auto-grow
            dense
            outlined
            hide-details
          ></v-textarea>
          <span class="form-hint">
            Placeholders:
            <code v-pre>{{validTo}}</code>
            <code v-pre>{{validFrom}}</code>
            <code v-pre>{{daysToExpiry}}</code>
            <code v-pre>{{leadDays}}</code>
            <code v-pre>{{sendDate}}</code>
          </span>
        </div>

        <div class="form-actions">
          <button
            class="form-btn form-btn--ghost"
            :disabled="reminderSaving || reminderTesting"
            @click="sendTestEmail()"
          >
            <v-icon size="16" class="mr-1">mdi-send-outline</v-icon>
            {{ reminderTesting ? "Sending…" : "Send test email" }}
          </button>
          <button
            class="form-btn form-btn--primary"
            :disabled="reminderSaving"
            @click="saveReminderSetting()"
          >
            <v-icon size="16" class="mr-1">mdi-content-save-outline</v-icon>
            {{ reminderSaving ? "Saving…" : "Save reminder" }}
          </button>
        </div>
      </div>
    </div>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
  </v-container>
</template>

<script>
import moment from "moment";
import Api from "@/objects/api";
import { mapState } from "vuex";
import Model from "@/objects/model";
import Service from "@/objects/service";
import hrdcFunction from "@/objects/hrdcFunction";
import HrdcData from "@/services/hrdc_event/objects/globalData";

export default {
  mixins: [hrdcFunction],
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
    previewSendDate() {
      // Read leadDays first so Vue always tracks it as a dependency — even on
      // the first evaluation, before latestToken has loaded. Otherwise the
      // early return below short-circuits before leadDays is read, Vue never
      // registers it, and the banner won't update when "Send before expiry"
      // changes.
      const days = Number(this.reminder.leadDays) || 0;
      if (!this.latestToken || !this.latestToken.validTo) return null;
      return moment(this.latestToken.validTo)
        .subtract(days, "days")
        .format("YYYY-MM-DD");
    },
    sendHour: {
      get() {
        return (this.reminder.sendTime || "08:00").split(":")[0];
      },
      set(val) {
        const minute =
          (this.reminder.sendTime || "08:00").split(":")[1] || "00";
        this.reminder.sendTime = `${val}:${minute}`;
      },
    },
    sendMinute: {
      get() {
        return (this.reminder.sendTime || "08:00").split(":")[1] || "00";
      },
      set(val) {
        const hour = (this.reminder.sendTime || "08:00").split(":")[0] || "08";
        this.reminder.sendTime = `${hour}:${val}`;
      },
    },
  },
  props: ["params"],
  data: () => ({
    apiGetXeroToken: new Api(),
    model: new Model(),
    service: new Service(),
    hrdcData: new HrdcData(),
    alertError: [],
    errorField: {
      applicationNo: null,
    },
    loadingDialog: false,
    headers: [
      {
        text: "No.",
        align: "start",
        sortable: false,
        value: "index",
      },
      { text: "Valid From", value: "validFrom", sortable: false },
      { text: "Valid To", value: "validTo", sortable: false },
      { text: "Generate Date", value: "timestampCreated", sortable: false },
    ],
    xeroDatas: [],
    activeTab: "history",
    apiGetReminder: new Api(),
    apiSaveReminder: new Api(),
    apiTestReminder: new Api(),
    reminder: {
      enabled: false,
      leadDays: 3,
      sendTime: "08:00",
      recipients: [],
      subject: "",
      body: "",
    },
    latestToken: null,
    reminderSaving: false,
    reminderTesting: false,
    hourOptions: Array.from({ length: 24 }, (_, i) =>
      String(i).padStart(2, "0")
    ),
    minuteOptions: Array.from({ length: 60 }, (_, i) =>
      String(i).padStart(2, "0")
    ),
  }),
  async created() {
    this.showLoadingDialog();

    // Developer console shows the single combined Event XERO token, which
    // resolves to the first registered event module's service (as today).
    const { getAllEventConfigs } = require("@/services/hrdc_event/eventRegistry");
    const firstConfig = getAllEventConfigs()[0];
    if (firstConfig) {
      this.service.set("key", firstConfig.serviceKey);
    }
    if (!this.model.name) this.model.name = {};
    this.model.name.xero_token = "XERO Token";

    // Assign apiDetails by mapping repository.
    const xeroSettingBase = `${
      this.$service[this.service.key]
    }/v1/en/console/settings/xero`;

    const apiDetails = {
      apiGetXeroToken: {
        url: `${xeroSettingBase}/get-xero-token-setting`,
      },
      apiGetReminder: {
        url: `${xeroSettingBase}/get-xero-token-reminder-setting`,
      },
      apiSaveReminder: {
        url: `${xeroSettingBase}/save-xero-token-reminder-setting`,
        method: "post",
      },
      apiTestReminder: {
        url: `${xeroSettingBase}/send-xero-token-reminder-test`,
        method: "post",
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    this.stages = await this.hrdcData.allStages;
    // No role gating in the developer console — the secret key is the authorization.
    this.xeroDatas = [];

    this.apiGetXeroToken.setCallbackCompleted((response) => {
      try {
        const { status, data } = response;

        if (!status) {
          this.$store.dispatch("showMessage", {
            message: data,
            messageType: "error",
            timeout: 2000,
          });

          return;
        }

        if (status) {
          if (data) {
            let index = 1;
            for (const item of data) {
              const { id, validFrom, validTo, timestampCreated } = item;

              this.xeroDatas.push({
                id,
                index,
                timestampCreated: moment(timestampCreated).format(
                  "YYYY-MM-DD HH:mm:ss"
                ),
                validFrom: moment(validFrom).format("YYYY-MM-DD"),
                validTo: moment(validTo).format("YYYY-MM-DD"),
              });
              index++;
            }
          }
        }
        this.hideLoadingDialog();
      } catch (err) {
        console.log(err);
      }
    });
    this.apiGetXeroToken.fetch();

    this.apiGetReminder.setCallbackCompleted((response) => {
      const { status, data } = response;
      if (status && data) {
        const c = data.config || {};
        this.reminder.enabled = !!c.enabled;
        this.reminder.leadDays = c.leadDays != null ? c.leadDays : 3;
        this.reminder.sendTime = c.sendTime || "08:00";
        this.reminder.recipients = Array.isArray(c.recipients)
          ? c.recipients
          : [];
        this.reminder.subject = c.subject || "";
        this.reminder.body = c.body || "";
        this.latestToken = data.latestToken || null;
      }
    });
    this.apiGetReminder.fetch();

    this.hideLoadingDialog();
  },
  methods: {
    redirectBack() {
      this.$router.push({ name: "PageDeveloperSettings" }).catch(() => {});
    },
    saveReminderSetting() {
      if (this.reminderSaving) return;
      this.reminderSaving = true;
      this.apiSaveReminder.setParams({
        enabled: this.reminder.enabled,
        leadDays: Number(this.reminder.leadDays) || 0,
        sendTime: this.reminder.sendTime,
        recipients: this.reminder.recipients,
        subject: this.reminder.subject,
        body: this.reminder.body,
      });
      this.apiSaveReminder.setCallbackCompleted((response) => {
        this.reminderSaving = false;
        const { status, data } = response;
        this.$store.dispatch("showMessage", {
          message:
            typeof data === "string"
              ? data
              : status
              ? "Reminder setting saved."
              : "Failed to save reminder setting.",
          messageType: status ? "success" : "error",
          timeout: 2500,
        });
        // Refresh preview (latest token / computed send date).
        if (status) this.apiGetReminder.fetch();
      });
      this.apiSaveReminder.setCallbackError(() => {
        this.reminderSaving = false;
      });
      this.apiSaveReminder.fetch();
    },
    sendTestEmail() {
      if (this.reminderTesting) return;
      if (!this.reminder.recipients || this.reminder.recipients.length === 0) {
        this.$store.dispatch("showMessage", {
          message: "Add at least one recipient before sending a test.",
          messageType: "error",
          timeout: 2500,
        });
        return;
      }
      this.reminderTesting = true;
      this.apiTestReminder.setParams({ recipients: this.reminder.recipients });
      this.apiTestReminder.setCallbackCompleted((response) => {
        this.reminderTesting = false;
        const { status, data } = response;
        this.$store.dispatch("showMessage", {
          message:
            typeof data === "string"
              ? data
              : status
              ? "Test email sent."
              : "Failed to send test email.",
          messageType: status ? "success" : "error",
          timeout: 3000,
        });
      });
      this.apiTestReminder.setCallbackError(() => {
        this.reminderTesting = false;
      });
      this.apiTestReminder.fetch();
    },
    generateXeroToken() {
      this.showLoadingDialog();
      // Round-trip through the event service's own Xero callback. This redirect_uri
      // is the one registered in the Xero app and must match the callback handler's
      // getRedirectUri() exactly (same suffix logic), else Xero rejects it with
      // "Invalid redirect_uri". `state` doubles as the applicationUuid ("token")
      // under which the settings-level refresh token is stored.
      const config = require("@/services/hrdc_event/eventRegistry").getEventConfig(
        this.service.key
      );
      const suffix = config
        ? config.pathSuffix
        : this.service.key.replace("hrdc_", "");
      const redirectCallbackUri = `${process.env.VUE_APP_URL}/en/service/${this.service.key}/callback_xero_${suffix}`;
      const xeroParams = {
        redirectCallbackUri,
        stateType: "token",
      };
      const childWindow = window.open(this.requestAuthorizeApp(xeroParams));
      var timer = setInterval(() => {
        if (childWindow.closed) {
          clearInterval(timer);
        }
      }, 500);
      this.hideLoadingDialog();
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .job-page {
  background-color: var(--app-page);
}

.dark-mode .job-header {
  background: var(--app-elevated);
}

.dark-mode .back-btn {
  background: var(--app-card);
  border: 1px solid var(--app-border-strong);
}

.dark-mode .back-btn:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

.dark-mode .hero-action-btn--blue {
  background: var(--app-accent-soft);
}

.dark-mode .section-card {
  background: var(--app-elevated);
}

.dark-mode .section-body {
  background: var(--app-card);
}

.dark-mode .job-table >>> .v-data-table-header {
  background: var(--app-surface);
}

.dark-mode .job-table >>> .v-data-table-header th {
  color: var(--app-text-muted) !important;
  border-bottom: 2px solid var(--app-border) !important;
}

.dark-mode .job-table >>> tbody tr:hover {
  background: var(--app-surface) !important;
}

.dark-mode .job-table >>> tbody td {
  border-bottom: 1px solid var(--app-border) !important;
  color: var(--app-text);
}

.dark-mode .job-table >>> .v-data-footer {
  border-top: 1px solid var(--app-border) !important;
  background: var(--app-surface);
}

.dark-mode .job-table >>> .v-data-footer__select {
  color: var(--app-text-muted) !important;
}

.dark-mode .job-table >>> .v-data-footer__select .v-input__slot {
  background: var(--app-card) !important;
  border: 1px solid var(--app-border) !important;
}

.dark-mode .job-table >>> .v-data-footer__pagination {
  color: var(--app-text-muted) !important;
}

.dark-mode .job-table >>> .v-data-footer__icons-before .v-btn,
.dark-mode .job-table >>> .v-data-footer__icons-after .v-btn {
  background: var(--app-card) !important;
  border: 1px solid var(--app-border) !important;
}

.dark-mode .job-table >>> .v-data-footer__icons-before .v-btn:hover:not(.v-btn--disabled),
.dark-mode .job-table >>> .v-data-footer__icons-after .v-btn:hover:not(.v-btn--disabled) {
  background: var(--app-accent-soft) !important;
}

.dark-mode .job-table >>> .v-data-footer__icons-before .v-btn .v-icon,
.dark-mode .job-table >>> .v-data-footer__icons-after .v-btn .v-icon {
  color: var(--app-text-muted) !important;
}

.dark-mode .table-index {
  background: var(--app-elevated);
  color: var(--app-text-muted);
}

.dark-mode .table-date {
  color: var(--app-text);
}

.dark-mode .table-empty__text {
  color: var(--app-text-faint);
}

.dark-mode .tab-btn {
  color: var(--app-text-muted);
  background: var(--app-elevated);
}

.dark-mode .tab-btn:hover {
  background: var(--app-accent-soft);
}

.dark-mode .tab-btn--active {
  background: var(--app-card);
}

.dark-mode .reminder-preview {
  background: var(--app-accent-soft);
}

.dark-mode .reminder-preview--off {
  background: var(--app-elevated);
  color: var(--app-text-muted);
}

.dark-mode .reminder-switch__text {
  color: var(--app-text);
}

.dark-mode .form-label {
  color: var(--app-text-muted);
}

.dark-mode .form-hint {
  color: var(--app-text-faint);
}

.dark-mode .form-hint code {
  background: var(--app-elevated);
}

.dark-mode .form-actions {
  border-top: 1px solid var(--app-border);
}

.dark-mode .form-btn--ghost {
  background: var(--app-elevated);
  color: var(--app-neutral-fg);
}

.dark-mode .form-btn--ghost:hover:not(:disabled) {
  background: var(--app-elevated);
}

.dark-mode .section-body--form >>> .v-text-field .v-input__slot {
  background: var(--app-card) !important;
  border: 1px solid var(--app-border) !important;
}

.dark-mode .section-body--form >>> .v-text-field input,
.dark-mode .section-body--form >>> .v-text-field textarea {
  color: var(--app-text) !important;
}

.dark-mode .section-body--form >>> .v-text-field input::placeholder,
.dark-mode .section-body--form >>> .v-text-field textarea::placeholder {
  color: var(--app-text-faint) !important;
}

.dark-mode .section-body--form >>> .v-text-field__suffix {
  color: var(--app-text-faint) !important;
}

.dark-mode .section-body--form >>> .v-select__selections .v-chip {
  background: var(--app-accent-soft) !important;
}

.dark-mode .time-colon {
  color: var(--app-text-muted);
}

.dark-mode .time-select >>> .v-select__selection {
  color: var(--app-text) !important;
}

.dark-mode .time-select >>> .v-input__append-inner .v-icon {
  color: var(--app-text-faint) !important;
}

/* >>> end auto-dark <<< */

/* ============================================
   PAGE WRAPPER
   ============================================ */
.job-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding-top: 24px !important;
  padding-bottom: 60px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ============================================
   HEADER
   ============================================ */
.job-header {
  background: #eaecef;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  margin-bottom: 24px;
  overflow: hidden;
}

.job-header__top {
  display: flex;
  align-items: center;
  padding: 24px 28px;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #d1d5db;
  cursor: pointer;
  margin-right: 16px;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.job-header__info {
  flex: 1;
  min-width: 0;
}

.job-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #3a3f47;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: -0.2px;
}

.job-header__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

/* ============================================
   ACTION BUTTONS
   ============================================ */
.hero-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border-radius: 12px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
}

.hero-action-btn--blue {
  background: #dbeafe;
  color: #1d4ed8;
}

.hero-action-btn--blue:hover {
  background: #bfdbfe;
  color: #1e40af;
}

/* ============================================
   SECTION CARDS
   ============================================ */
.section-card {
  background: #eaecef;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
  margin-bottom: 24px;
}

.section-card:hover {
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);
}

.section-header {
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  padding: 13px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-header__title {
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.section-header__count {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.72rem;
  font-weight: 500;
}

.section-header__status {
  display: inline-flex;
  align-items: center;
  font-weight: 700;
  color: #fff;
}

.section-body {
  background: #fff;
}

.section-body--table {
  padding: 0;
}

/* ============================================
   DATA TABLE
   ============================================ */
.job-table {
  background: transparent !important;
  box-shadow: none !important;
}

.job-table >>> .v-data-table-header {
  background: #f8fafc;
}

.job-table >>> .v-data-table-header th {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  color: #64748b !important;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-bottom: 2px solid #e2e8f0 !important;
  padding: 14px 16px !important;
  white-space: nowrap;
}

.job-table >>> tbody tr {
  transition: background 0.15s ease;
}

.job-table >>> tbody tr:hover {
  background: #f8fafc !important;
}

.job-table >>> tbody td {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.85rem !important;
  font-weight: 900 !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
  color: #0f172a;
}

.job-table >>> .v-data-footer {
  border-top: 1px solid #e2e8f0 !important;
  font-family: 'Lato', sans-serif !important;
  padding: 12px 20px !important;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.job-table >>> .v-data-footer__select {
  font-size: 0.78rem !important;
  color: #64748b !important;
  font-weight: 600;
  margin-right: 16px !important;
}

.job-table >>> .v-data-footer__select .v-select {
  font-family: 'Lato', sans-serif !important;
  margin: 0 0 0 8px !important;
}

.job-table >>> .v-data-footer__select .v-input__slot {
  background: #fff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  min-height: 32px !important;
  padding: 0 8px !important;
  box-shadow: none !important;
}

.job-table >>> .v-data-footer__select .v-input__slot::before,
.job-table >>> .v-data-footer__select .v-input__slot::after {
  display: none !important;
}

.job-table >>> .v-data-footer__pagination {
  font-size: 0.78rem !important;
  color: #64748b !important;
  font-weight: 600;
  margin: 0 8px !important;
}

.job-table >>> .v-data-footer__icons-before,
.job-table >>> .v-data-footer__icons-after {
  display: inline-flex;
  gap: 4px;
}

.job-table >>> .v-data-footer__icons-before .v-btn,
.job-table >>> .v-data-footer__icons-after .v-btn {
  width: 32px !important;
  height: 32px !important;
  background: #fff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  margin: 0 2px !important;
  transition: all 0.15s ease;
}

.job-table >>> .v-data-footer__icons-before .v-btn:hover:not(.v-btn--disabled),
.job-table >>> .v-data-footer__icons-after .v-btn:hover:not(.v-btn--disabled) {
  background: #e0f2fe !important;
  border-color: #0ea5e9 !important;
}

.job-table >>> .v-data-footer__icons-before .v-btn--disabled,
.job-table >>> .v-data-footer__icons-after .v-btn--disabled {
  opacity: 0.4 !important;
}

.job-table >>> .v-data-footer__icons-before .v-btn .v-icon,
.job-table >>> .v-data-footer__icons-after .v-btn .v-icon {
  color: #64748b !important;
  font-size: 18px !important;
}

/* Table cell styles */
.table-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 700;
}

.table-error {
  color: #dc2626;
  font-size: 0.82rem;
}

.table-date {
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  color: #0f172a;
  font-weight: 500;
}

/* Empty state */
.table-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 0;
}

.table-empty__text {
  font-size: 0.88rem;
  color: #94a3b8;
  font-weight: 500;
}

/* ============================================
   TRANSITIONS
   ============================================ */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 959px) {
  .job-header__top {
    padding: 16px 20px;
  }
}

/* ============================================
   DARK MODE
   ============================================ */
.dark-mode .job-page { background-color: var(--app-page); }
.dark-mode .job-header { background: var(--app-card); }
.dark-mode .job-header__title { color: var(--app-text); }
.dark-mode .back-btn { background: var(--app-page); border-color: var(--app-border); }
.dark-mode .back-btn:hover { background: var(--app-card); }
.dark-mode .section-card { background: var(--app-card); }
.dark-mode .section-body { background: var(--app-card); }
.dark-mode .job-table >>> .v-data-table-header { background: var(--app-page); }
.dark-mode .job-table >>> tbody td { color: var(--app-text) !important; border-color: var(--app-border) !important; }
.dark-mode .job-table >>> tbody tr:hover { background: var(--app-border) !important; }
.dark-mode .job-table >>> .v-data-footer { background: var(--app-page); border-color: var(--app-border) !important; }
.dark-mode .table-index { background: var(--app-border); color: var(--app-text-muted); }
.dark-mode .table-date { color: var(--app-text); }

/* ============================================
   TAB BAR
   ============================================ */
.tab-bar {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  padding: 10px 18px;
  border-radius: 12px;
  font-family: 'Lato', sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  color: #64748b;
  background: #eaecef;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-btn:hover {
  color: #0369a1;
  background: #e0f2fe;
}

.tab-btn--active {
  color: #0369a1;
  background: #fff;
  border-color: #bae6fd;
  box-shadow: 0 1px 4px rgba(14, 165, 233, 0.15);
}

/* ============================================
   REMINDER FORM
   ============================================ */
.section-body--form {
  padding: 24px;
}

.reminder-preview {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 10px;
  background: #e0f2fe;
  color: #0c4a6e;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 22px;
}

.reminder-preview strong {
  font-weight: 800;
}

.reminder-preview--off {
  background: #f1f5f9;
  color: #64748b;
}

.form-row {
  margin-bottom: 18px;
}

.reminder-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
}
/* Keep the switch sized to its control so only the control toggles —
   not the full-width row / label. */
.reminder-switch {
  flex: 0 0 auto;
  margin: 0;
  padding: 0;
}
.reminder-switch >>> .v-input__slot {
  margin-bottom: 0;
}
.reminder-switch__text {
  font-size: 0.85rem;
  font-weight: 700;
  color: #334155;
  user-select: none;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-bottom: 18px;
}

.form-field {
  margin-bottom: 18px;
  display: flex;
  flex-direction: column;
}

.form-grid .form-field {
  margin-bottom: 0;
}

.form-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.form-hint {
  margin-top: 8px;
  font-size: 0.74rem;
  color: #94a3b8;
}

.form-hint code {
  background: #f1f5f9;
  color: #0369a1;
  padding: 1px 6px;
  border-radius: 5px;
  font-size: 0.72rem;
  margin: 0 2px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
}

.form-btn {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  border-radius: 12px;
  font-family: 'Lato', sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
}

.form-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.form-btn--primary {
  background: #0ea5e9;
  color: #fff;
}

.form-btn--primary:hover:not(:disabled) {
  background: #0284c7;
}

.form-btn--ghost {
  background: #f1f5f9;
  color: #475569;
}

.form-btn--ghost:hover:not(:disabled) {
  background: #e2e8f0;
}

/* Reminder form — dark mode */
.dark-mode .tab-btn { background: var(--app-card); color: var(--app-text-muted); }
.dark-mode .tab-btn:hover { background: var(--app-accent-soft); color: var(--app-accent-strong); }
.dark-mode .tab-btn--active { background: var(--app-page); color: var(--app-accent-strong); border-color: var(--app-accent); }
.dark-mode .reminder-preview { background: var(--app-accent-soft); color: var(--app-accent-strong); }
.dark-mode .reminder-preview--off { background: var(--app-page); color: var(--app-text-muted); }
.dark-mode .form-label { color: var(--app-text-muted); }
.dark-mode .reminder-switch__text { color: var(--app-neutral-fg); }
.dark-mode .form-hint code { background: var(--app-page); color: var(--app-accent-strong); }
.dark-mode .form-actions { border-color: var(--app-border); }
.dark-mode .form-btn--ghost { background: var(--app-border); color: var(--app-neutral-fg); }
.dark-mode .form-btn--ghost:hover:not(:disabled) { background: var(--app-border-strong); }

/* ============================================
   HRDC-THEME INPUT FIELDS (Tailwind-style)
   Boxed fields — white bg, slate border, rounded, sky focus ring — to match
   the rest of the console. Targets every text-field / combobox / textarea in
   the reminder form (v-switch excluded: it is a toggle, not a boxed field).
   ============================================ */
.section-body--form >>> .v-text-field .v-input__slot {
  background: #fff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
  min-height: 44px !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
/* Drop the outlined <fieldset> entirely — its <legend> cut the blue notch in
   the top border. The border now lives on the input slot itself, so the box
   is a clean, continuous rounded rectangle. */
.section-body--form >>> .v-text-field fieldset {
  border: none !important;
}
.section-body--form >>> .v-text-field.v-input--is-focused .v-input__slot {
  border-color: #0ea5e9 !important;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15) !important;
}
.section-body--form >>> .v-text-field input,
.section-body--form >>> .v-text-field textarea {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.88rem !important;
  font-weight: 600;
  color: #0f172a !important;
}
.section-body--form >>> .v-text-field input::placeholder,
.section-body--form >>> .v-text-field textarea::placeholder {
  color: #94a3b8 !important;
  font-weight: 500;
}
.section-body--form >>> .v-text-field__suffix {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.78rem !important;
  font-weight: 600;
  color: #94a3b8 !important;
}
.section-body--form >>> .v-select__selections .v-chip {
  background: #e0f2fe !important;
  color: #0369a1 !important;
  font-family: 'Lato', sans-serif !important;
  font-weight: 700 !important;
  border-radius: 6px !important;
  margin: 3px 4px 3px 0 !important;
}
.section-body--form >>> .v-select__selections .v-chip .v-icon {
  color: #0369a1 !important;
}
.section-body--form >>> .v-select__selections input {
  font-weight: 600;
}

/* Send-out-time hour : minute dropdowns */
.time-dropdowns {
  display: flex;
  align-items: center;
  gap: 10px;
}
.time-select {
  flex: 1;
}
.time-colon {
  font-size: 1.15rem;
  font-weight: 800;
  color: #64748b;
}
.time-select >>> .v-select__selection {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.9rem !important;
  font-weight: 700 !important;
  color: #0f172a !important;
}
.time-select >>> .v-input__append-inner .v-icon {
  color: #94a3b8 !important;
}

/* HRDC-theme inputs — dark mode */
.dark-mode .section-body--form >>> .v-text-field .v-input__slot { background: var(--app-page) !important; border-color: var(--app-border) !important; }
.dark-mode .section-body--form >>> .v-text-field.v-input--is-focused .v-input__slot { border-color: var(--app-accent) !important; }
.dark-mode .section-body--form >>> .v-text-field input,
.dark-mode .section-body--form >>> .v-text-field textarea { color: var(--app-text) !important; }
.dark-mode .section-body--form >>> .v-select__selections .v-chip { background: var(--app-accent-soft) !important; color: var(--app-accent-strong) !important; }
.dark-mode .section-body--form >>> .v-select__selections .v-chip .v-icon { color: var(--app-accent-strong) !important; }
.dark-mode .time-colon { color: var(--app-text-muted); }
.dark-mode .time-select >>> .v-select__selection { color: var(--app-text) !important; }

/* >>> dark companions: the light rules above pin these to a dark ink
   with !important, which on the dark field renders the text invisible
   rather than absent. Token references only. <<< */

.dark-mode .section-body--form >>> .v-text-field input,
.dark-mode .section-body--form >>> .v-text-field textarea {
  color: var(--app-text) !important;
}
</style>

<style>
/* The unscoped `.v-menu__content …` block that used to sit here is gone.
   It was global — unscoped CSS is injected once and never removed, so a rule
   written for THIS page's dropdown repainted every menu in the app (with
   !important, and with hard-coded light hex) for the rest of the session. That
   is what made the same v-select look different depending on which page you had
   visited. Menus are defined ONCE, in tokens, in src/plugins/theme-glass.css §3
   — panel, list, item radius, hover, active, typography and the state overlay.
   A page that genuinely needs its own menu styling should give that v-menu a
   `content-class` and key off it, the way .bdt-menu and .tb-view-menu do. */
</style>
